const AXES = [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1],
];

const MAX_GENERATION_ATTEMPTS = 320;
const MAX_MUTATION_ATTEMPTS = 240;

const dot = (left, right) => left.reduce((sum, value, index) => sum + (value * right[index]), 0);
const cross = (left, right) => [
    (left[1] * right[2]) - (left[2] * right[1]),
    (left[2] * right[0]) - (left[0] * right[2]),
    (left[0] * right[1]) - (left[1] * right[0]),
];

// These are the 24 orientation-preserving rotations of a cube. Reflections are
// deliberately excluded so a mirrored chiral shape remains a valid distractor.
const CUBE_ROTATIONS = AXES.flatMap((xAxis) => (
    AXES
        .filter((yAxis) => dot(xAxis, yAxis) === 0)
        .map((yAxis) => [xAxis, yAxis, cross(xAxis, yAxis)])
));

const mulberry32 = (initialSeed) => {
    let seed = initialSeed >>> 0;
    return () => {
        let value = seed += 0x6D2B79F5;
        value = Math.imul(value ^ (value >>> 15), value | 1);
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
};

const shuffleWith = (values, random) => {
    const result = [...values];
    for (let index = result.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(random() * (index + 1));
        [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }
    return result;
};

const voxelKey = ([x, y, z]) => `${x},${y},${z}`;

const rotateVoxel = ([x, y, z], [xAxis, yAxis, zAxis]) => [
    (x * xAxis[0]) + (y * yAxis[0]) + (z * zAxis[0]),
    (x * xAxis[1]) + (y * yAxis[1]) + (z * zAxis[1]),
    (x * xAxis[2]) + (y * yAxis[2]) + (z * zAxis[2]),
];

const normalizeVoxels = (voxels) => {
    const minX = Math.min(...voxels.map(([x]) => x));
    const minY = Math.min(...voxels.map(([, y]) => y));
    const minZ = Math.min(...voxels.map(([, , z]) => z));
    return voxels
        .map(([x, y, z]) => [x - minX, y - minY, z - minZ])
        .sort(([leftX, leftY, leftZ], [rightX, rightY, rightZ]) => (
            (leftZ - rightZ) || (leftY - rightY) || (leftX - rightX)
        ));
};

const voxelSignature = (voxels) => normalizeVoxels(voxels).map((voxel) => voxel.join(',')).join('|');

const canonicalVoxelSignature = (voxels) => CUBE_ROTATIONS
    .map((rotation) => voxelSignature(voxels.map((voxel) => rotateVoxel(voxel, rotation))))
    .sort()[0];

const mirrorVoxels = (voxels) => voxels.map(([x, y, z]) => [-x, y, z]);

const isConnectedVoxels = (voxels) => {
    if (!voxels.length) {
        return false;
    }

    const available = new Set(voxels.map(voxelKey));
    const visited = new Set([voxelKey(voxels[0])]);
    const queue = [voxels[0]];

    while (queue.length) {
        const [x, y, z] = queue.shift();
        AXES.forEach(([offsetX, offsetY, offsetZ]) => {
            const neighbor = [x + offsetX, y + offsetY, z + offsetZ];
            const key = voxelKey(neighbor);
            if (available.has(key) && !visited.has(key)) {
                visited.add(key);
                queue.push(neighbor);
            }
        });
    }

    return visited.size === voxels.length;
};

const getShapeSpans = (voxels) => [0, 1, 2].map((axis) => (
    (Math.max(...voxels.map((voxel) => voxel[axis])) - Math.min(...voxels.map((voxel) => voxel[axis]))) + 1
));

const isUsablePolycube = (voxels) => {
    const spans = getShapeSpans(voxels);
    return isConnectedVoxels(voxels)
        && spans.every((span) => span >= 2)
        && Math.max(...spans) <= 5;
};

const isChiral = (voxels) => (
    canonicalVoxelSignature(voxels) !== canonicalVoxelSignature(mirrorVoxels(voxels))
);

const collectFrontier = (voxels) => {
    const occupied = new Set(voxels.map(voxelKey));
    const frontier = new Map();

    voxels.forEach(([x, y, z]) => {
        AXES.forEach(([offsetX, offsetY, offsetZ]) => {
            const candidate = [x + offsetX, y + offsetY, z + offsetZ];
            const key = voxelKey(candidate);
            if (!occupied.has(key)) {
                frontier.set(key, candidate);
            }
        });
    });

    return [...frontier.values()];
};

const growPolycube = (count, random) => {
    const voxels = [[0, 0, 0]];
    while (voxels.length < count) {
        const frontier = collectFrontier(voxels);
        const candidate = frontier[Math.floor(random() * frontier.length)];
        voxels.push(candidate);
    }
    return normalizeVoxels(voxels);
};

const createConnectedPolycube = (count, random, excludedSignatures = new Set()) => {
    for (let attempt = 0; attempt < MAX_GENERATION_ATTEMPTS; attempt += 1) {
        const candidate = growPolycube(count, random);
        const signature = canonicalVoxelSignature(candidate);
        if (
            isUsablePolycube(candidate)
            && isChiral(candidate)
            && !excludedSignatures.has(signature)
        ) {
            return candidate;
        }
    }

    // A chiral tetracube with a tail remains chiral as it grows. This fallback
    // keeps game creation deterministic even after an unusually poor random run.
    const fallback = [[0, 0, 0], [0, 1, 0], [1, 1, 0], [0, 0, 1]];
    while (fallback.length < count) {
        const frontier = collectFrontier(fallback);
        const next = frontier.find((candidate) => {
            const shape = normalizeVoxels([...fallback, candidate]);
            return isUsablePolycube(shape) && isChiral(shape);
        }) || frontier[0];
        fallback.push(next);
    }

    return normalizeVoxels(fallback);
};

const moveOneBlock = (voxels, random) => {
    const removalOrder = shuffleWith(voxels.map((_, index) => index), random);

    for (const removalIndex of removalOrder) {
        const remaining = voxels.filter((_, index) => index !== removalIndex);
        if (!isConnectedVoxels(remaining)) {
            continue;
        }

        const removedKey = voxelKey(voxels[removalIndex]);
        const additions = shuffleWith(collectFrontier(remaining), random)
            .filter((candidate) => voxelKey(candidate) !== removedKey);

        for (const addition of additions) {
            const candidate = normalizeVoxels([...remaining, addition]);
            if (isUsablePolycube(candidate)) {
                return candidate;
            }
        }
    }

    return voxels;
};

const mutateBlocks = (source, moveCount, random, excludedSignatures) => {
    for (let attempt = 0; attempt < MAX_MUTATION_ATTEMPTS; attempt += 1) {
        let candidate = source;
        for (let move = 0; move < moveCount; move += 1) {
            candidate = moveOneBlock(candidate, random);
        }

        const signature = canonicalVoxelSignature(candidate);
        if (!excludedSignatures.has(signature) && isUsablePolycube(candidate)) {
            return candidate;
        }
    }

    return createConnectedPolycube(source.length, random, excludedSignatures);
};

const pickDistinctRotation = (voxels, random, excludedSignatures = new Set()) => {
    const rotations = shuffleWith(CUBE_ROTATIONS, random);
    for (const rotation of rotations) {
        const rotated = voxels.map((voxel) => rotateVoxel(voxel, rotation));
        const signature = voxelSignature(rotated);
        if (!excludedSignatures.has(signature)) {
            return { rotated, signature };
        }
    }

    const rotated = voxels.map((voxel) => rotateVoxel(voxel, rotations[0]));
    return { rotated, signature: voxelSignature(rotated) };
};

const getMutationMoves = (difficulty, decoyIndex) => {
    if (difficulty <= 1) {
        return decoyIndex === 0 ? 3 : 2;
    }
    if (difficulty === 2) {
        return decoyIndex === 0 ? 2 : 1;
    }
    return 1;
};

const createVoxelTurnRound = (roundIndex = 1, runSeed = Date.now(), requestedDifficulty = 1) => {
    const difficulty = Math.max(1, Math.min(6, Math.round(requestedDifficulty)));
    const seed = (
        (runSeed >>> 0)
        ^ Math.imul(roundIndex, 0x9E3779B1)
        ^ Math.imul(difficulty, 0x85EBCA6B)
    ) >>> 0;
    const random = mulberry32(seed);
    const cubeCount = 4 + difficulty;
    const baseVoxels = createConnectedPolycube(cubeCount, random);
    const baseCanonical = canonicalVoxelSignature(baseVoxels);
    const excludedShapes = new Set([baseCanonical]);
    const decoys = [];

    if (difficulty >= 3) {
        const mirrored = normalizeVoxels(mirrorVoxels(baseVoxels));
        const mirrorSignature = canonicalVoxelSignature(mirrored);
        if (mirrorSignature !== baseCanonical) {
            excludedShapes.add(mirrorSignature);
            decoys.push({ kind: 'mirror', voxels: mirrored });
        }
    }

    while (decoys.length < 3) {
        const moveCount = getMutationMoves(difficulty, decoys.length);
        const decoy = mutateBlocks(baseVoxels, moveCount, random, excludedShapes);
        const signature = canonicalVoxelSignature(decoy);
        if (!excludedShapes.has(signature)) {
            excludedShapes.add(signature);
            decoys.push({ kind: moveCount === 1 ? 'one-block' : 'reshaped', voxels: decoy });
        }
    }

    const referenceResult = pickDistinctRotation(baseVoxels, random);
    const correctResult = pickDistinctRotation(baseVoxels, random, new Set([referenceResult.signature]));
    const correctId = `correct-${seed}`;
    const options = [
        { id: correctId, kind: 'same', voxels: correctResult.rotated },
        ...decoys.map((decoy, index) => ({
            id: `decoy-${seed}-${index}`,
            kind: decoy.kind,
            voxels: pickDistinctRotation(decoy.voxels, random).rotated,
        })),
    ];

    return {
        correctId,
        difficulty,
        id: `${runSeed}-${roundIndex}-${difficulty}`,
        options: shuffleWith(options, random),
        reference: referenceResult.rotated,
    };
};

export {
    CUBE_ROTATIONS,
    canonicalVoxelSignature,
    createVoxelTurnRound,
    isConnectedVoxels,
    mirrorVoxels,
    normalizeVoxels,
    voxelSignature,
};
